// One trip in the month ledger, as the app's TripRowView lays it out.

import { CategoryBadge, CategoryChip } from "../../components/Category.tsx";
import { Icon } from "../../components/Icon.tsx";
import type { Category } from "../../domain/categories.ts";
import { formatDistance, formatDuration, formatMoney, formatTimeRange } from "../../domain/format.ts";
import { type Trip, spokenTripTitle, tripDuration, tripTitle } from "../../domain/model.ts";
import type { DistanceUnit } from "../../domain/rates.ts";

export interface QuickPick {
  category: Category;
  chartColor: string;
}

export function TripRow({
  trip, category, unit, value, currencyCode, vehicleName, dayStart, isSelected, quickPicks, onOpen, onClassify, undo,
}: {
  trip: Trip;
  category: Category | undefined;
  unit: DistanceUnit;
  value: number | undefined;
  currencyCode: string;
  /** Shown when the account has more than one vehicle. */
  vehicleName: string | undefined;
  /** The first trip of its day shows the day's number and weekday. */
  dayStart: boolean;
  isSelected: boolean;
  /** Business, Personal, Commute: inline buttons on unclassified trips. */
  quickPicks: QuickPick[];
  onOpen: () => void;
  onClassify: (category: Category) => void;
  /** Review mode keeps a just-classified trip in place with Undo. */
  undo?: () => void;
}) {
  const duration = tripDuration(trip);
  const title = tripTitle(trip);
  const showsPurpose = category?.isDeductible && trip.purpose;

  return (
    <div
      className={`cv-row${dayStart ? " is-day-start" : ""}${isSelected ? " is-selected" : ""}`}
      onClick={(event) => { if (!(event.target as HTMLElement).closest("button")) onOpen(); }}
    >
      <div className="cv-day" aria-hidden={!dayStart}>
        {dayStart && <><b>{trip.start.getDate()}</b><span>{trip.start.toLocaleDateString(undefined, { weekday: "short" })}</span></>}
      </div>
      <CategoryBadge category={category} />
      <div style={{ minWidth: 0 }}>
        <div className="cv-row-title">
          <button type="button" className="t" onClick={onOpen} aria-current={isSelected ? "true" : undefined} aria-label={`${spokenTripTitle(trip)}, ${trip.start.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}`}>
            {title}
          </button>
          {trip.isRoundTrip && <Icon name="roundTrip" label="Round trip" />}
        </div>
        <div className="cv-row-meta">
          <CategoryChip category={category} />
          {category && trip.classificationSource && <Icon name="wand" label="Classified automatically" />}
          <span>{formatTimeRange(trip.start, trip.end)}{duration !== undefined ? ` · ${formatDuration(duration)}` : ""}</span>
          {vehicleName && <span className="cv-meta-group"><span className="sep" aria-hidden="true">·</span><span>{vehicleName}</span></span>}
          {undo && <span className="cv-meta-group"><span className="sep" aria-hidden="true">·</span><button type="button" className="cv-undo" onClick={undo}>Undo</button></span>}
        </div>
        {showsPurpose && <div className="cv-purpose">{trip.purpose}</div>}
        {!category && quickPicks.length > 0 && (
          <div className="cv-quick" role="group" aria-label={`Classify ${spokenTripTitle(trip)}`}>
            {quickPicks.map((pick) => (
              <button key={pick.category.rawValue} type="button" style={{ "--tint": pick.chartColor } as React.CSSProperties} onClick={() => onClassify(pick.category)}>
                <span className="dot" aria-hidden="true" />{pick.category.name}
              </button>
            ))}
            <button type="button" onClick={onOpen}>More…</button>
          </div>
        )}
      </div>
      <div className="cv-figs">
        <b>{formatDistance(trip.meters, unit)}</b>
        {category && (value !== undefined
          ? <span>{formatMoney(value, currencyCode)}</span>
          : <span className="none" aria-label="No mileage value">—</span>)}
      </div>
    </div>
  );
}
